import { Outlet } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Logo } from "@/components/cinch/Logo";
import { SignInParticles } from "@/components/cinch/SignInParticles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { usePageTracking } from "@/lib/analytics";
import { useAuthState } from "@/lib/auth";

import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";

function DevSignInForm() {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [email, setEmail] = useState("jrae@cinch.io");
  const [companies, setCompanies] = useState("*");

  // Probe the backend once. The endpoint returns 200 only when test mode is on.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/test-available", { credentials: "include" })
      .then((r) => !cancelled && setAvailable(r.ok))
      .catch(() => !cancelled && setAvailable(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (available !== true) return null;

  const signIn = () => {
    const params = new URLSearchParams({ email, companies, next: "/" });
    window.location.href = `/api/auth/dev-signin?${params.toString()}`;
  };

  return (
    <div className="mt-6 rounded-lg border border-dashed border-brand/50 bg-brand/5 p-4 text-left">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand">
        Dev sign-in (side door)
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        No OAuth client at engine-dev yet. This mints a local JWT and sets your
        cookie. Disabled automatically when{" "}
        <code className="rounded bg-muted px-1">CINCH_OPS_OAUTH_TEST_MODE</code>{" "}
        is off.
      </p>
      <div className="flex flex-col gap-2">
        <div>
          <Label htmlFor="dev-email" className="text-xs">
            Email
          </Label>
          <Input
            id="dev-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@cinch.io"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="dev-companies" className="text-xs">
            Companies (CSV or *)
          </Label>
          <Input
            id="dev-companies"
            value={companies}
            onChange={(e) => setCompanies(e.target.value)}
            placeholder="* or uuid1,uuid2"
            className="mt-1"
          />
        </div>
        <Button onClick={signIn} className="mt-2">
          Sign in as {email}
        </Button>
      </div>
    </div>
  );
}

export function AppShell() {
  const { user, isLoading } = useAuthState();

  // Unconditional — hooks can't sit behind the early returns below. Harmless
  // while signed out: the analytics client only starts once a user is known,
  // so the sign-in screen emits nothing.
  usePageTracking();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  // Unauthenticated landing: real OAuth button + (when test mode is on) a dev side door.
  if (!user) {
    return (
      <div className="relative flex h-screen items-center justify-center bg-background">
        <SignInParticles />
        <div className="relative z-10 w-full max-w-sm rounded-xl border bg-card p-8 text-center shadow-sm">
          <Logo variant="wordmark" tone="brand" className="mx-auto mb-1 h-7" />
          <p className="mb-6 text-sm text-muted-foreground">
            Operations
          </p>
          <a
            href="/api/auth/login"
            className="inline-block rounded-md bg-power px-5 py-2 text-sm font-semibold text-white hover:bg-power-hover"
          >
            Sign in
          </a>
          <DevSignInForm />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          <TopBar />
          <main className="flex-1 overflow-auto p-6">
            <Outlet />
          </main>
        </div>
      </div>
      <Toaster />
    </div>
  );
}
